<template>
  <main class="main-content">
    <div class="max-w-6xl mx-auto">
      <!-- 页面标题 -->
      <div class="mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">常见问题解答</h1>
        <p class="text-gray-600">查找API使用过程中遇到的常见问题及解决方案</p>
      </div>

      <!-- 搜索区域 -->
      <Card class="mb-6">
        <CardContent class="p-4">
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
            />
            <input
              v-model="searchQuery"
              placeholder="搜索问题..."
              class="pl-10 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </CardContent>
      </Card>

      <!-- 分类筛选 -->
      <div class="flex flex-wrap gap-2 mb-6">
        <button
          v-for="category in categories"
          :key="category.id"
          @click="selectCategory(category.id)"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium',
            selectedCategory === category.id
              ? 'bg-custom-purple text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
          ]"
        >
          {{ category.name }}
        </button>
      </div>

      <!-- 常见问题列表 -->
      <div class="space-y-4">
        <Card v-for="faq in filteredFaqs" :key="faq.id" class="overflow-hidden">
          <CardHeader
            class="cursor-pointer flex flex-row items-center justify-between"
            @click="toggleFaq(faq.id)"
          >
            <CardTitle class="text-base font-medium">{{
              faq.question
            }}</CardTitle>
            <ChevronDown
              class="h-4 w-4 transition-transform duration-200"
              :class="{ 'rotate-180': expandedFaqs.includes(faq.id) }"
            />
          </CardHeader>
          <CardContent
            v-show="expandedFaqs.includes(faq.id)"
            class="pt-0 border-t"
          >
            <div class="pt-4 text-gray-600" v-html="faq.answer"></div>
          </CardContent>
        </Card>
      </div>

      <!-- 联系支持 -->
      <!-- <Card class="mt-12 text-center">
        <CardContent class="p-8">
          <div
            class="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4"
          >
            <HelpCircle class="h-6 w-6 text-blue-600" />
          </div>
          <h3 class="text-xl font-semibold mb-2">没有找到您需要的问题？</h3>
          <p class="text-gray-600 mb-4">我们的技术支持团队随时为您提供帮助</p>
          <button
            class="px-4 py-2 bg-custom-purple text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-custom-purple focus:ring-offset-2"
          >
            联系技术支持
          </button>
        </CardContent>
      </Card> -->
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Sidebar from "./Sidebar.vue";
import Card from "./ui/Card.vue";
import CardHeader from "./ui/CardHeader.vue";
import CardTitle from "./ui/CardTitle.vue";
import CardContent from "./ui/CardContent.vue";
import { Search, ChevronDown, HelpCircle } from "lucide-vue-next";
import { useCodeHighlight } from "@/composables/useCodeHighlight";
import { useSystemStore } from "@/stores/system";

// 使用代码高亮功能
useCodeHighlight("pre code");

// 系统配置
const systemStore = useSystemStore();

// 分类数据
const categories = [
  { id: "all", name: "全部" },
  { id: "authentication", name: "身份验证" },
  { id: "signing", name: "签约流程" },
  { id: "data", name: "数据查询" },
  { id: "payment", name: "支付相关" },
  { id: "technical", name: "技术问题" },
];

// FAQ数据
const faqs = ref([
  {
    id: 1,
    question: "如何获取AppID和API Key？",
    answer:
      '<p>在银企通™ API平台注册后，进入"应用与设置"页面，您可以创建应用并获取对应的AppID和API Key。',
    category: "authentication",
  },
  {
    id: 2,
    question: "支持哪些银行？",
    answer:
      '<p>关于支持的国内外银行的清单，请参考<a href="https://bank.xencio.com/map/" target="_blank" class="text-[var(--primary)] hover:underline">银行列表</a>。</p>',
    category: "signing",
  },
  {
    id: 3,
    question: "如何获取签约链接？",
    answer:
      '<p>调用"获取签约链接"接口可以获取云直连签约网站的URL。</p><a href="sign-url" target="_blank" class="text-[var(--primary)] hover:underline">查看“获取签约链接”接口详情</a>',
    category: "signing",
  },
  {
    id: 4,
    question: "签约流程需要多长时间？",
    answer:
      '<p>签约流程的时间因银行而异，通常需要1-3个工作日。部分银行支持实时签约，但大多数银行需要人工审核。</p><p>您可以通过调用"获取签约状态查询/数据同步状态"接口来监控签约进度。</p>',
    category: "signing",
  },
  {
    id: 5,
    question: "如何查询银行流水？",
    answer:
      '<p>当签约状态变为"数据同步完成"后，您可以调用"流水查询接口"获取银行流水数据：</p><a href="transaction-query" target="_blank" class="text-[var(--primary)] hover:underline">查看“流水查询接口”详情</a>',
    category: "data",
  },
  {
    id: 6,
    question: "数据同步需要多长时间？",
    answer:
      '<p>签约成功后，银企通™ API平台会自动同步该账号最近30天的流水数据。同步时间取决于银行和数据量，通常需要几分钟到几小时。</p><p>您可以通过调用状态查询接口监控同步进度，直到状态变为"数据同步完成"。</p>',
    category: "data",
  },
  {
    id: 7,
    question: "支付功能支持哪些银行？",
    answer:
      "<p>目前银企通™ API平台支持以下银行的支付功能：工商银行、建设银行、农业银行、中国银行、交通银行、招商银行、民生银行、中信银行、光大银行、华夏银行、广发银行、平安银行、浦发银行、兴业银行、邮储银行等主流商业银行。</p><p>具体支持情况可能会因银行政策调整而变化，建议在签约前确认银行支持情况。</p>",
    category: "payment",
  },
  {
    id: 8,
    question: "沙盒环境和生产环境有什么区别？",
    answer: `<p>沙盒环境用于开发和测试，提供模拟数据，不会产生真实的银行交易。生产环境用于正式业务，连接真实的银行系统。</p><p>两个环境的API地址、AppID和API Key都不同：</p><ul class="list-disc pl-5 space-y-1"><li><strong>沙盒环境：</strong>Server: <span id="faq-sandbox-url">${systemStore.sandboxUrl}</span></li><li><strong>生产环境：</strong>Server: <span id="faq-production-url">${systemStore.productionUrl}</span></li></ul>`,
    category: "technical",
  },
  {
    id: 9,
    question: "如何获取生产环境的调用凭证？",
    answer:
      "<p>完成沙盒环境测试后，您需要联系银企通™ API平台客服获取生产环境的调用地址、AppID和API Key。</p><p>由于部署方式可能存在SaaS和本地化两种形式，生产环境的API地址会因客户而异，请务必联系客服获取准确信息。</p>",
    category: "technical",
  },
  {
    id: 10,
    question: "API调用频率有限制吗？",
    answer:
      '<p>是的，为了保护系统稳定性，银企通™ API平台对API调用频率有一定限制：</p><ul class="list-disc pl-5 space-y-1"><li>沙盒环境：每分钟最多100次调用</li><li>生产环境：根据客户等级和套餐不同，限制会有所区别</li></ul><p>如果您的应用需要更高的调用频率，请联系客服申请调整限制。</p>',
    category: "technical",
  },
]);

const selectedCategory = ref("all");
const searchQuery = ref("");
const expandedFaqs = ref<number[]>([]);

// 切换FAQ展开/收起状态
const toggleFaq = (id: number) => {
  const index = expandedFaqs.value.indexOf(id);
  if (index > -1) {
    expandedFaqs.value.splice(index, 1);
  } else {
    expandedFaqs.value.push(id);
  }
};

// 选择分类
const selectCategory = (categoryId: string) => {
  selectedCategory.value = categoryId;
};

// 过滤后的FAQ列表
const filteredFaqs = computed(() => {
  return faqs.value.filter((faq) => {
    const matchesCategory =
      selectedCategory.value === "all" ||
      faq.category === selectedCategory.value;
    const matchesSearch =
      searchQuery.value === "" ||
      faq.question.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.value.toLowerCase());
    return matchesCategory && matchesSearch;
  });
});
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
}

code {
  font-size: 0.875rem;
}

/* 自定义紫色背景 */
.bg-custom-purple {
  background-color: #623bee;
}

/* 悬停效果 */
.bg-custom-purple:hover {
  background-color: #5835d6; /* 稍微深一点的颜色 */
}
</style>
